/**
 * shell/app-shell.ts
 * Shell entry point — initialises all shell subsystems on DOMContentLoaded.
 *
 * Replaces: shell/app-shell.js
 * Improvements over JS version:
 *   - All imports are typed (.ts modules)
 *   - window.ToolNexusShell is declared with an explicit interface
 *   - initShell() is guarded against double-initialisation
 *   - Global changeTextSize / switchRailTab shims kept for Razor inline
 *     handlers during the Phase 4.8 transition (removed when onclick= gone)
 */
import { initThemeController, applyTheme } from './theme-controller.js';
import { initThemeSwitcher } from './theme-switcher.js';
import { initThemeModal } from './theme-modal.js';
import { initAccountDropdown } from './account-dropdown.js';
import { initLanguageSwitcher } from './language-switcher.js';
import { initClearRecentTools } from './clear-recent-tools.js';
import { initTextScaleController, changeTextSize } from './text-scale-controller.js';
import { initAccessibilityController } from './accessibility-controller.js';
import { initFocusMode, toggleFocus, enterFocus, exitFocus } from './focus-mode.js';
import { initSidebarFilter } from './sidebar-filter.js';
import { initSidebarPin } from './sidebar-state.js';
import { initSidebarGroups } from './sidebar-groups.js';
import { initToolPublicSidebar } from './tool-public-sidebar.js';
import { initMobileNav } from './mobile-nav.js';
import { initHeaderScroll } from './header-scroll.js';
import { initPlatformShortcut } from './platform-shortcut.js';
import { initKbdHints } from './kbd-hint.js';
import { initLocalProcessingStrip } from './local-processing-strip.js';
import { initAdSlots } from './ad-slot.js';
import { AppState } from './state.js';
import { queryAll } from '../types/dom.js';
// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------
const INIT_KEY = 'tnShellInit'; // ← was 'tn-shell-init' — DOMStringMap requires camelCase
function isAlreadyInitialised() {
    return document.documentElement.dataset[INIT_KEY] === 'true';
}
function markInitialised() {
    document.documentElement.dataset[INIT_KEY] = 'true';
}
function initRailTabControls() {
    queryAll('[data-rail-tab]').forEach((button) => {
        button.addEventListener('click', () => {
            const tab = button.dataset['railTab'];
            if (tab !== undefined && tab.length > 0) {
                window.switchRailTab?.(tab);
            }
        });
    });
}
function hideFooterAds() {
    queryAll('#sticky-footer-ad').forEach((ad) => {
        ad.hidden = true;
    });
}
function recordFooterAdImpression() {
    const ad = document.querySelector('#sticky-footer-ad[data-affiliate-impression]');
    const href = ad?.dataset.affiliateImpression;
    if (!href)
        return;
    try {
        void fetch(href, { method: 'POST', credentials: 'same-origin', keepalive: true }).catch(() => { });
    }
    catch {
        /* telemetry must never break the UI */
    }
}
function initFooterAdControls() {
    if (AppState.footerAd.isDismissed()) {
        hideFooterAds();
        return;
    }
    // The offer is visible → record one impression (cache-safe: fires client-side
    // even when the page HTML was served from the anon output cache).
    recordFooterAdImpression();
    // Provider fill no longer happens here — initAdSlots() is hoisted above this
    // function in initShell(), so the in-content unit initializes even when the footer
    // is dismissed. initAdSlots() itself skips any slot inside a hidden subtree, so the
    // dismissed-footer promise (never request a slot the visitor won't see) still holds.
    queryAll('[data-footer-ad-dismiss]').forEach((button) => {
        button.addEventListener('click', hideFooterAds);
    });
    queryAll('[data-footer-ad-dismiss-permanent]').forEach((button) => {
        button.addEventListener('click', () => {
            AppState.footerAd.dismiss();
            hideFooterAds();
        });
    });
}
// ---------------------------------------------------------------------------
// Shell init
// ---------------------------------------------------------------------------
function initShell() {
    if (isAlreadyInitialised())
        return;
    markInitialised();
    // ── Core subsystems ───────────────────────────────────────────────────────
    initThemeController();
    initThemeSwitcher();
    initThemeModal();
    initTextScaleController();
    initAccessibilityController();
    // `initOmnibox()` was called here. `shell/omnibox.js` is deleted: it early-returned on every
    // page because it anchored on `#search-input-main` and `#search-palette`, markup commit
    // 825c713c0 (2026-06-23) removed from the last view that carried it. Measured absent on all five
    // shell surfaces. Its function — a Ctrl+K command palette — is served by
    // `command-palette-loader.js` + `command-palette.js` + `command-registry.js`, which is what
    // actually opens on Ctrl+K today. See plan §19 finding #121.
    initFocusMode();
    initSidebarFilter();
    /* Groups must expand before initSidebarPin's auto-scroll runs, otherwise the
       active link is inside a collapsed region and has no real offsetTop. */
    initSidebarGroups();
    initSidebarPin();
    initToolPublicSidebar();
    initMobileNav();
    initHeaderScroll();
    initAccountDropdown();
    // Header dropdown hygiene for the language <details>: outside-click close,
    // Escape close + focus restore, and mutual exclusion with the other header
    // popovers (account menu / display settings / command palette).
    initLanguageSwitcher();
    initClearRecentTools();
    initPlatformShortcut();
    // Must run before first paint of any _KbdHint badge so the modifier
    // spelling never flashes the wrong glyph.
    initKbdHints();
    initLocalProcessingStrip();
    initRailTabControls();
    // Ad slots are filled regardless of the footer-dismissal state. The old placement — inside
    // initFooterAdControls, below the dismissed early return — meant one permanent dismissal
    // killed every ad slot on the site, including the in-content unit on tool pages that is
    // independent of the footer. A dismissed footer is hidden first, so initAdSlots() skips it
    // via its `[hidden]`-ancestor guard while still filling the in-content slot.
    if (AppState.footerAd.isDismissed()) {
        hideFooterAds();
    }
    initAdSlots();
    initFooterAdControls();
    // Expose AppState to global window for non-module scripts (like tool-studio-review-init.js)
    window.AppState = AppState;
    // ── Public API ────────────────────────────────────────────────────────────
    window.ToolNexusShell = Object.freeze({
        toggleFocus,
        enterFocus,
        exitFocus,
        applyTheme,
        changeTextSize: (d) => changeTextSize(d === 1 ? 1 : -1),
    });
    // ── Legacy shims for Razor inline onclick= handlers ───────────────────────
    // These allow existing .cshtml files that use onclick="typeof X === 'function' ? X() : null"
    // to keep working until Phase 4.8 removes all inline handlers.
    window.changeTextSize = (d) => changeTextSize(d === 1 ? 1 : -1);
    window.toggleFocusModeOn = () => toggleFocus();
    // switchRailTab is used by the settings button in _SharedShellHeader.cshtml.
    // It is a no-op shim here — the actual implementation lives in the tool
    // shell JS (toolshell-v13.css companion script) and is set by that module.
    if (window.switchRailTab === undefined) {
        window.switchRailTab = (_tab) => {
            // No-op on pages without the tool shell. The tool shell overrides this.
        };
    }
}
// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initShell, { once: true });
}
else {
    // DOMContentLoaded already fired (e.g. script loaded with defer)
    initShell();
}
//# sourceMappingURL=app-shell.js.map